import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { AppShell } from "@/shared/components/layout/AppShell";
import { AccountSectionLayout } from "@/shared/components/layout/AccountSectionLayout";
import { RestaurantLayout } from "@/features/restaurants/layouts/RestaurantLayout";
import { SettingsLayout } from "@/features/restaurants/layouts/SettingsLayout";
import { OnboardingGuard } from "@/features/restaurants/components/OnboardingGuard";
import { SubscriptionGuard } from "@/features/subscriptions/components/SubscriptionGuard";

// User pages
const AccountDashboard = lazy(() => import("@/features/user/pages/AccountDashboard"));
const AccountSettings = lazy(() => import("@/features/user/pages/AccountSettings"));
const Billing = lazy(() => import("@/features/subscriptions/pages/Billing"));
const AccountPaymentsPage = lazy(() =>
  import("@/features/payments").then((m) => ({ default: m.AccountPaymentsPage }))
);
const PayoutsPage = lazy(() =>
  import("@/features/payments").then((m) => ({ default: m.PayoutsPage }))
);
const StripeReturnPage = lazy(() =>
  import("@/features/payments").then((m) => ({ default: m.StripeReturnPage }))
);

// Restaurant pages
const CreateRestaurant = lazy(() => import("@/features/restaurants/pages/CreateRestaurant"));
const RestaurantList = lazy(() => import("@/features/restaurants/pages/RestaurantList"));
const RestaurantDashboard = lazy(() => import("@/features/restaurants/pages/RestaurantDashboard"));
const RestaurantOnboarding = lazy(() => import("@/features/restaurants/pages/RestaurantOnboarding"));
const MenuManagement = lazy(() => import("@/features/menu/pages/MenuManagement"));
const CategoryManagement = lazy(() => import("@/features/menu/pages/CategoryManagement"));
const AddonManagement = lazy(() => import("@/features/menu/pages/AddonManagement"));
const OrdersManagement = lazy(() =>
  import("@/features/orders").then((m) => ({ default: m.OrdersManagement }))
);
const OrderDetail = lazy(() =>
  import("@/features/orders").then((m) => ({ default: m.OrderDetail }))
);
const OrderHistory = lazy(() =>
  import("@/features/orders").then((m) => ({ default: m.OrderHistory }))
);
const AnalyticsDashboard = lazy(() =>
  import("@/features/analytics").then((m) => ({ default: m.AnalyticsDashboard }))
);
const ReservationsManagement = lazy(() =>
  import("@/features/reservations/pages/ReservationsManagement")
);
const DiscountsManagement = lazy(() =>
  import("@/features/discounts/pages/DiscountsManagement")
);
const ZonesSettings = lazy(() => import("@/features/restaurants/pages/ZonesSettings"));
const PaymentSettings = lazy(() => import("@/features/restaurants/pages/PaymentSettings"));
const Integrations = lazy(() => import("@/features/restaurants/pages/Integrations"));
const PublicProfileSettings = lazy(() =>
  import("@/features/restaurants/pages/PublicProfileSettings")
);
const LegalSettings = lazy(() => import("@/features/restaurants/pages/LegalSettings"));

// Notifications
const NotificationSettings = lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.NotificationSettings }))
);
const AccountNotificationsPage = lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.AccountNotificationsPage }))
);

// Printer settings (Electron-only)
const PrinterSettings = lazy(() =>
  import("@/features/printer").then((m) => ({ default: m.PrinterSettings }))
);

// Restaurant Settings pages
const SettingsIndex = lazy(() => import("@/features/restaurants/pages/settings/index"));
const BasicInfoSettings = lazy(() =>
  import("@/features/restaurants/pages/settings/BasicInfoSettings")
);
const MediaSettings = lazy(() => import("@/features/restaurants/pages/settings/MediaSettings"));
const ContactSettings = lazy(() =>
  import("@/features/restaurants/pages/settings/ContactSettings")
);
const BusinessSettings = lazy(() =>
  import("@/features/restaurants/pages/settings/BusinessSettings")
);
const HoursSettings = lazy(() => import("@/features/restaurants/pages/settings/HoursSettings"));
const SeoSettings = lazy(() => import("@/features/restaurants/pages/settings/SeoSettings"));
const CustomDomainSettings = lazy(() =>
  import("@/features/restaurants/pages/settings/CustomDomainSettings")
);
const EstimatesSettings = lazy(() =>
  import("@/features/restaurants/pages/settings/EstimatesSettings")
);

/**
 * Private Routes Configuration
 *
 * Authenticated owner/admin routes. Everything inside <AppShell/> shares one
 * persistent sidebar that adapts between account and restaurant context based
 * on whether the URL carries a `:restaurantId`.
 *
 * All page components are lazy-loaded so they are excluded from the initial
 * bundle downloaded by storefront visitors.
 */
export const privateRoutes = (
    <>
        {/* Stripe redirect shims — kept outside the shell so the brief
            navigation doesn't flash chrome. They protect themselves. */}
        <Route
            path="/app/settings/payments/return"
            element={
                <ProtectedRoute>
                    <StripeReturnPage variant="return" />
                </ProtectedRoute>
            }
        />
        <Route
            path="/app/settings/payments/refresh"
            element={
                <ProtectedRoute>
                    <StripeReturnPage variant="refresh" />
                </ProtectedRoute>
            }
        />

        {/* App shell — persistent sidebar + top bar for every authenticated page. */}
        <Route
            element={
                <ProtectedRoute>
                    <AppShell />
                </ProtectedRoute>
            }
        >
            {/* Workspace */}
            <Route path="/app" element={<AccountDashboard />} />
            <Route path="/app/restaurants" element={<RestaurantList />} />
            <Route
                path="/app/create-restaurant"
                element={
                    <SubscriptionGuard>
                        <CreateRestaurant />
                    </SubscriptionGuard>
                }
            />

            {/* Account section — tabbed sub-nav across profile, billing, payments, payouts. */}
            <Route element={<AccountSectionLayout />}>
                <Route path="/app/settings" element={<AccountSettings />} />
                <Route path="/app/billing" element={<Billing />} />
                <Route path="/app/settings/payments" element={<SubscriptionGuard fallbackPath="/app/billing"><AccountPaymentsPage /></SubscriptionGuard>} />
                <Route path="/app/settings/payments/payouts" element={<SubscriptionGuard fallbackPath="/app/billing"><PayoutsPage /></SubscriptionGuard>} />
                <Route path="/app/settings/notifications" element={<AccountNotificationsPage />} />
                <Route path="/app/settings/printer" element={<PrinterSettings />} />
            </Route>

            {/* Restaurant onboarding — sidebar visible; OnboardingGuard handles redirects on other restaurant routes. */}
            <Route
                path="/app/restaurants/:restaurantId/onboarding"
                element={<RestaurantOnboarding />}
            />

            {/* Restaurant scope — single source of restaurant data + nested pages. */}
            <Route
                path="/app/restaurants/:restaurantId"
                element={
                    <OnboardingGuard>
                        <RestaurantLayout />
                    </OnboardingGuard>
                }
            >
                <Route index element={<RestaurantDashboard />} />

                <Route path="menu" element={<MenuManagement />} />
                <Route path="categories" element={<CategoryManagement />} />
                <Route path="addons" element={<AddonManagement />} />
                <Route path="orders" element={<OrdersManagement />} />
                <Route path="orders/history" element={<OrderHistory />} />
                <Route path="orders/:orderId" element={<OrderDetail />} />
                <Route path="reservations" element={<ReservationsManagement />} />
                <Route path="discounts" element={<DiscountsManagement />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />

                {/* Settings — single sidebar entry with tabbed sub-pages. */}
                <Route element={<SettingsLayout />}>
                    <Route path="settings" element={<SettingsIndex />} />
                    <Route path="settings/basic" element={<BasicInfoSettings />} />
                    <Route path="settings/media" element={<MediaSettings />} />
                    <Route path="settings/contact" element={<ContactSettings />} />
                    <Route path="settings/business" element={<BusinessSettings />} />
                    <Route path="settings/hours" element={<HoursSettings />} />
                    <Route path="settings/domain" element={<CustomDomainSettings />} />
                    <Route path="settings/seo" element={<SeoSettings />} />
                    <Route path="settings/zones" element={<ZonesSettings />} />
                    <Route path="settings/delivery" element={<Navigate to="../zones" replace />} />
                    <Route path="settings/pickup" element={<Navigate to="../zones" replace />} />
                    <Route path="settings/estimates" element={<EstimatesSettings />} />
                    <Route path="settings/delivery-settings" element={<Navigate to="../zones" replace />} />
                    <Route path="settings/payment-settings" element={<PaymentSettings />} />
                    <Route path="settings/notifications" element={<NotificationSettings />} />
                </Route>

                <Route path="subscription" element={<Navigate to="/app/billing" replace />} />
                <Route path="integrations" element={<Integrations />} />
                <Route path="profile" element={<PublicProfileSettings />} />
                <Route path="legal" element={<LegalSettings />} />
            </Route>
        </Route>

        {/* Legacy redirects */}
        <Route
            path="/app/restaurants/:restaurantId/menu/addons"
            element={<Navigate to="../../addons" replace />}
        />
        <Route
            path="/app/restaurants/:restaurantId/edit"
            element={<Navigate to="settings" replace />}
        />
        <Route
            path="/app/restaurants/:restaurantId/delivery"
            element={<Navigate to="settings/zones" replace />}
        />
        <Route
            path="/app/restaurants/:restaurantId/hours"
            element={<Navigate to="settings/hours" replace />}
        />
        <Route
            path="/app/restaurants/:restaurantId/payments"
            element={<Navigate to="settings/payment-settings" replace />}
        />
    </>
);
